import { supabase } from "./supabaseClient";
import { loadReportData, loadTypeOverrides, loadListingLinks } from "./reportLoader";

export { lookupTowns } from "./reportLoader";

// Business type corrections and listing links belong to one account
// (agencyId): each account keeps its own, and changing one never changes
// another account's reports.

// Everything marketOverview() and advertiserOverview() need for one job:
// { job, runs, samples, searches, appearances, typeOverrides, listingLinks, towns, now }
// Loaded by the code shared with the MCP server (reportLoader.js).
export function fetchMarketOverviewData(jobId, agencyId) {
  return loadReportData(supabase, jobId, { agencyId });
}

// Approved corrections only: [{ key, type }].
export function fetchTypeOverrides(agencyId) {
  return loadTypeOverrides(supabase, agencyId);
}

// Saves a business type correction for the account. It applies straight away.
// Returns an error message, or null.
export async function saveTypeOverride(key, type, agencyId) {
  if (!agencyId) return "No account";
  const { data: auth } = await supabase.auth.getUser();
  const { error } = await supabase.from("advertiser_types").upsert(
    { agency_id: agencyId, key, type, status: "approved", set_by: auth?.user?.id || null, updated_at: new Date().toISOString() },
    { onConflict: "agency_id,key" }
  );
  if (error) console.error("saveTypeOverride", error);
  return error ? error.message : null;
}

// Removes the account's correction, so AdAudit's own classification is used again.
export async function clearTypeOverride(key, agencyId) {
  if (!agencyId) return "No account";
  const { error } = await supabase.from("advertiser_types").delete().eq("agency_id", agencyId).eq("key", key);
  if (error) console.error("clearTypeOverride", error);
  return error ? error.message : null;
}

// ---------- Map pack listing links (advertiser_links) ----------

// Applied links only: [{ listing_key, domain, action, status }].
// If the table isn't there yet, the report still works without links.
export function fetchListingLinks(agencyId) {
  return loadListingLinks(supabase, agencyId);
}

// Saves a link for a listing with no website (listing key "name:...").
//   action "link":   the listing joins the website in domain
//   action "unlink": a likely match is split back out (domain is ignored)
// It applies straight away. Returns an error message, or null.
export async function saveListingLink(listingKey, action, domain, agencyId) {
  if (!agencyId) return "No account";
  const { data: auth } = await supabase.auth.getUser();
  const { error } = await supabase.from("advertiser_links").upsert(
    {
      agency_id: agencyId,
      listing_key: listingKey,
      action,
      domain: action === "link" ? domain : null,
      status: "applied",
      set_by: auth?.user?.id || null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "agency_id,listing_key" }
  );
  if (error) console.error("saveListingLink", error);
  return error ? error.message : null;
}

// Removes the account's link for a listing, so AdAudit's own matching is used again.
export async function clearListingLink(listingKey, agencyId) {
  if (!agencyId) return "No account";
  const { error } = await supabase.from("advertiser_links").delete().eq("agency_id", agencyId).eq("listing_key", listingKey);
  if (error) console.error("clearListingLink", error);
  return error ? error.message : null;
}
